import { formatDateToDateString } from "@components/inputs/date/utils";
import { TGetValueFn } from "@components/smart/FieldInfo";
import BindingContext, { IEntity, TEntityKey } from "@odata/BindingContext";
import { IAssetEntity, IDocumentEntity } from "@odata/GeneratedEntityTypes";
import { AssetItemTypeCode } from "@odata/GeneratedEnums";
import { fetchDataAndParseError, getAsset } from "@pages/asset/Asset.utils";
import {
    FixedAssetFormViewAction,
    getActionType,
    ILowerPriceCustomData
} from "@pages/asset/fixedAsset/FixedAsset.utils";
import { isNonProfitCompany } from "@utils/CompanyUtils";
import { memoizeOne } from "@utils/memoizeOne";

import { ASSET_API_URL } from "../../../constants";
import { getEntityMemoKey } from "../../../views/formView/Form.utils";
import { FormStorage } from "../../../views/formView/FormStorage";

export interface ILowerPriceDialogEntity extends IAssetEntity {
    AssetId?: TEntityKey;
}

export const LowerPriceDatePath = BindingContext.localContext("LowerPriceDate");
export const LowerPriceAmountPath = BindingContext.localContext("LowerPriceAmount");
export const LowerPriceDebitAccountPath = BindingContext.localContext("LowerPriceDebitAccount");
export const LowerPriceCreditAccountPath = BindingContext.localContext("LowerPriceCreditAccount");

const isOfActionType: (type: FixedAssetFormViewAction) => TGetValueFn<boolean> = (type) =>
    (args) => {
        const { storage } = args;
        const assetStorage = (storage as FormStorage<ILowerPriceDialogEntity, ILowerPriceCustomData>).getCustomData().assetStorage;
        const t = getActionType(assetStorage);
        return t === type;
    };

export const isApplyGrantType = isOfActionType(FixedAssetFormViewAction.ApplyGrant);
export const isLowerPriceType = isOfActionType(FixedAssetFormViewAction.LowerTaxPrice);

export const _getLowerTaxAccountLocalPath = (type: "Credit" | "Debit", subField?: "Name" | "Number"): string =>
    BindingContext.localContext(`LowerPrice${type}Account${subField ? `_${subField}` : ""}`);

interface IGrantAllocationParams {
    GrantEquityAccountId?: string;
    GrantOtherIncomeAccountId?: string;
    GrantAmount?: number;
}

interface ILowerTaxAssetPriceParams {
    AssetId: number;
    Date: string;
    GrantAllocationParameters: IGrantAllocationParams;
}

export function lowerTaxAssetPrice(storage: FormStorage, AssetId: number): Promise<boolean | IEntity> {
    const _isLowerPriceType = isLowerPriceType({ storage });
    const _isNonProfit = isNonProfitCompany(storage.context);

    const params: ILowerTaxAssetPriceParams = {
        AssetId,
        Date: formatDateToDateString(storage.getValueByPath(LowerPriceDatePath)),
        GrantAllocationParameters: {}
    };

    if (_isLowerPriceType) {
        params.GrantAllocationParameters.GrantAmount = storage.getValueByPath(LowerPriceAmountPath);
    }

    if (_isNonProfit) {
        params.GrantAllocationParameters.GrantEquityAccountId = storage.getValueByPath(LowerPriceDebitAccountPath);
        params.GrantAllocationParameters.GrantOtherIncomeAccountId = storage.getValueByPath(LowerPriceCreditAccountPath);
    }

    const endpoint = _isLowerPriceType
        ? (_isNonProfit ? AssetItemTypeCode.ReductionOfTaxPriceWithAccounting
            : AssetItemTypeCode.ReductionOfTaxPrice)
        : AssetItemTypeCode.GrantAllocatedToAsset;

    return fetchDataAndParseError(`${ASSET_API_URL}/${endpoint}`, params);
}

export interface IUnappliedGrantItem {
    Id: string;
    ShortGrantName: string;
    Document: Partial<IDocumentEntity>;
    UnappliedGrantAmount: number;
}

export const unappliedGrantItems = memoizeOne((storage: FormStorage): IUnappliedGrantItem[] => {
    const asset = getAsset({ storage });
    const appliedGrantEntries = new Set<number>();
    asset.Items.forEach(item => {
        item.GrantEntries?.forEach(ge => appliedGrantEntries.add(ge.Id));
    });
    const _createId = (docId: number, grantId: number) => `${docId}_${grantId}`;
    const unappliedItems = new Map<string, IUnappliedGrantItem>();
    asset.Items?.forEach(item => {
        const uGrantEntries = item.DocumentItem?.GrantEntries
            ?.filter(ge => !!ge.Amount && appliedGrantEntries.has(ge.Id) === false);
        if (item.ItemTypeCode === AssetItemTypeCode.Acquisition && uGrantEntries?.length) {
            uGrantEntries.forEach(ge => {
                const Id = _createId(item.Document.Id, ge.Grant.Id);
                const current = unappliedItems.get(Id) ?? {
                    Id,
                    ShortGrantName: ge.Grant.ShortName,
                    Document: item.Document,
                    UnappliedGrantAmount: 0
                };
                current.UnappliedGrantAmount += ge.Amount;
                unappliedItems.set(Id, current);
            });
        }
    });

    return unappliedItems.values().toArray();
}, (storage) => [getEntityMemoKey(storage)]);